import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ClipboardCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { LEAD_STATUS_META, formatUSD } from "@/lib/format";

const STATUS_OPTIONS = ["", "new", "contacted", "appt_scheduled", "showed_up", "no_show", "sold", "lost"];

const AdminLeads = () => {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState("");

  useEffect(() => {
    api.get("/admin/dealers").then((r) => setDealers(r.data)).catch(() => setDealers([]));
  }, []);

  const fetchData = () => {
    const params = new URLSearchParams({ page: String(page), page_size: "50" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (dealerId) params.set("dealer_id", dealerId);
    api.get(`/admin/leads?${params.toString()}`).then((r) => {
      setRows(r.data.items);
      setTotal(r.data.total);
    }).catch(() => setRows([]));
  };
  useEffect(() => { fetchData(); }, [page, status, dealerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); setPage(1); fetchData(); };

  return (
    <AdminPage title="All leads" subtitle="Every lead across every dealership." testId="admin-leads-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3 mb-4">
        <form onSubmit={onSearch} className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search name/email/phone/notes" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="admin-leads-search" />
        </form>
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-full md:w-40" data-testid="admin-leads-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{LEAD_STATUS_META[s]?.label || s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dealerId || "all"} onValueChange={(v) => { setDealerId(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-full md:w-56" data-testid="admin-leads-dealer-select"><SelectValue placeholder="Dealership" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All dealerships</SelectItem>
            {dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!rows ? (
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
          <ClipboardCheck size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500 text-sm">No leads match your filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Vehicle</th>
                  <th className="text-left px-4 py-3 font-semibold">Dealer</th>
                  <th className="text-left px-4 py-3 font-semibold">BDC</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const meta = LEAD_STATUS_META[l.status] || LEAD_STATUS_META.new;
                  return (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/leads/${l.id}`} className="text-brand font-medium hover:underline">
                          {l.customer?.full_name || l.guest_name || "—"}
                        </Link>
                        <p className="text-[11px] text-slate-500">{l.customer?.email || l.guest_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {l.vehicle ? (
                          <span>{l.vehicle.year} {l.vehicle.make} {l.vehicle.model}<span className="ml-2 text-[11px] text-slate-500">{formatUSD(l.vehicle.price, 0)}</span></span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{l.dealer?.name || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{l.bdc_agent?.full_name || <span className="text-slate-400 italic">Unclaimed</span>}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}>{meta.label}</span></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
            <span>Showing {rows.length} of {total.toLocaleString()}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</Button>
              <Button variant="outline" size="sm" disabled={rows.length < 50} onClick={() => setPage((p) => p + 1)}>Next →</Button>
            </div>
          </div>
        </>
      )}
    </AdminPage>
  );
};

export default AdminLeads;
